import React, { forwardRef, ElementType, ReactNode, useRef, useState, useCallback } from 'react';
import {
  Box,
  BoxProps,
  Card,
  CardProps,
  ClickAwayListener,
  ClickAwayListenerProps,
  Fade,
  FadeProps,
  IconButton,
  IconButtonProps,
  Popper,
  PopperProps,
  useMediaQuery,
  useTheme,
  SvgIconProps,
} from '@mui/material';
import { PlusIcon } from '@/src/components/icon/stroke/PlusIcon';
import { ResponsiveStyleValue } from '@mui/system';

export interface HotspotCardProps extends Omit<IconButtonProps, 'color' | 'size'> {
  /**
   * top position of hotspot card
   */
  top?: ResponsiveStyleValue<number | string>;
  /**
   * left position of hotspot card
   */
  left?: ResponsiveStyleValue<number | string>;
  /**
   * bottom position of hotspot card
   */
  bottom?: ResponsiveStyleValue<number | string>;
  /**
   * right position of hotspot card
   */
  right?: ResponsiveStyleValue<number | string>;
  /**
   * Boolean that defines different design whether a HotspotCardIconButton is placed on background inside the Card, (instead of image)
   */
  onBackground?: boolean;
  /**
   * Make sure to center IconButton, helpful with left or right properties set to 50%
   */
  center?: boolean;
  /**
   * Boolean that defines whether a HotspotCard is opened.
   *
   * @see setOpen
   */
  open?: boolean;
  /**
   * Function that can be used to control a state of card popup
   *
   * @param open new open value
   * @see open
   */
  setOpen?: (open: boolean) => void;
  /**
   * Properties that will be passed to Fade component
   *
   * @see Fade
   */
  fadeProps?: Omit<FadeProps, 'children'>;
  /**
   * Properties that will be passed to Popper component
   *
   * @see Popper
   */
  popperProps?: Omit<PopperProps, 'open'>;
  /**
   * Properties applied to ClickAwayListener
   */
  clickAwayListenerProps?: Omit<ClickAwayListenerProps, 'children'>;
  /**
   * Custom icon component to use instead of the default PlusIcon
   */
  icon?: ReactNode;
  /**
   * Properties that will be passed to the icon component
   */
  iconProps?: SvgIconProps;
  /**
   * Custom icon component to use instead of the default PlusIcon
   */
  insideIcon?: ReactNode;
  /**
   * Properties for a cose icon inside HotspotCard
   */
  insideIconProps?: SvgIconProps;
  /**
   * Properties for a IconButton inside HotspotCard
   */
  insideIconButtonProps?: IconButtonProps<ElementType>;
  /**
   * Properties for a Box element that wraps button and popper
   */
  wrapperProps?: BoxProps<ElementType>;
  /**
   * Properties for Card inside the Popper element.
   */
  cardProps?: CardProps<ElementType>;
}

/**
 * A card component that is toggled on and off with IconButton.
 * The card is fullscreen on mobile-size devices.
 */
export const HotspotCard: React.FC<HotspotCardProps> = forwardRef(
  (
    {
      sx,
      children,
      top,
      left,
      bottom,
      right,
      onBackground = false,
      center = false,
      open,
      setOpen,
      fadeProps = {},
      popperProps = {},
      clickAwayListenerProps = {},
      icon,
      iconProps = {},
      insideIcon,
      insideIconProps = {},
      insideIconButtonProps = {},
      wrapperProps = {},
      cardProps = {},
      ...restProps
    },
    ref,
  ) => {
    const { sx: cardSx, ...restCard } = cardProps;
    const { sx: popperSx, ...restPopperProps } = popperProps;
    const { sx: iconSx, ...restIconProps } = iconProps;
    const { sx: insideIconSx, ...restInsideIconProps } = insideIconProps;
    const { sx: insideIconButtonSx, ...restInsideIconButtonProps } = insideIconButtonProps;

    const FWLTheme = useTheme();
    const isSmallScreen = useMediaQuery(FWLTheme.breakpoints.down('md'));
    const btnRef = useRef(null);
    const popperRef = useRef<HTMLDivElement>(null);
    const [_open, _setOpen] = useState(open ?? false);
    const [currentPlacement, setCurrentPlacement] = useState<string>('left-start');

    const isOpen = open ?? _open;
    const handleClick = useCallback(open !== undefined ? () => setOpen(!open) : () => _setOpen((prev) => !prev), [
      open,
      setOpen,
    ]);
    const handleClickAway = useCallback(open !== undefined ? () => setOpen(false) : () => _setOpen(false), [
      open,
      setOpen,
    ]);

    return (
      <ClickAwayListener onClickAway={handleClickAway} {...clickAwayListenerProps}>
        <Box {...wrapperProps}>
          <IconButton
            ref={ref ?? btnRef}
            onClick={handleClick}
            aria-label="Open or Close the card"
            {...(isOpen && { ['aria-controls']: 'hotspot-card' })}
            size="small"
            color="surface"
            sx={{
              position: 'absolute',
              zIndex: (theme) => theme.zIndex.hotspotCard,
              ...(top !== undefined && { top: top, ...(center && { mt: '-16px' }) }),
              ...(left !== undefined && { left: left, ...(center && { ml: '-16px' }) }),
              ...(bottom !== undefined && { bottom: bottom, ...(center && { mb: '-16px' }) }),
              ...(right !== undefined && { right: right, ...(center && { mr: '-16px' }) }),
              // Fallback to top-left if no position is specified
              ...(top === undefined &&
                left === undefined &&
                bottom === undefined &&
                right === undefined && {
                  top: '0%',
                  left: '0%',
                  ...(center && { mt: '-16px', ml: '-16px' }),
                }),
              ...sx,
            }}
            {...restProps}
          >
            {icon || (
              <PlusIcon
                sx={{
                  transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                  transition: 'transform .3s ease',
                  ...iconSx,
                }}
                {...restIconProps}
              />
            )}
          </IconButton>
          <Popper
            key={isSmallScreen ? 'popper-mobile' : 'popper-desktop'}
            open={isOpen}
            anchorEl={isSmallScreen ? null : ((ref as React.RefObject<HTMLButtonElement>) ?? btnRef)?.current}
            transition
            placement="left-start"
            disablePortal
            ref={popperRef}
            modifiers={[
              {
                name: 'flip',
                enabled: true,
                options: {
                  fallbackPlacements: ['right-start'],
                },
              },
              {
                name: 'preventOverflow',
                enabled: true,
                options: {
                  boundary: 'viewport',
                },
              },
              {
                name: 'updatePlacement',
                enabled: true,
                phase: 'afterWrite',
                fn: ({ state }) => {
                  setCurrentPlacement(state.placement);
                },
              },
            ]}
            sx={{
              position: 'fixed',
              top: { xs: '0', md: 'auto' },
              left: { xs: '0', md: 'auto' },
              width: { xs: '100%', md: '400px' },
              height: { xs: '100%', md: 'auto' },
              minHeight: { xs: '100vh', md: 'auto' },
              zIndex: (theme) => theme.zIndex.modal,
              ...popperSx,
            }}
            {...restPopperProps}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350} {...fadeProps}>
                <Card
                  id="hotspot-card"
                  sx={{
                    boxShadow: (theme) => theme.flowShadows.xl,
                    position: 'relative',
                    width: { xs: '100%', md: 'inherit' },
                    height: { xs: '100%', md: 'auto' },
                    top: { xs: '0', md: '-9px' },
                    left: currentPlacement.startsWith('right') ? { xs: '0', md: '-41px' } : undefined,
                    right: currentPlacement.startsWith('left') ? { xs: '0', md: '-41px' } : undefined,
                    // remove border
                    '&:after': {
                      border: 'none',
                    },
                    // card wrapper
                    '& > .MuiBox-root': {
                      overflow: 'auto',
                    },
                    // media in card
                    '& .MuiCardMedia-root': {
                      height: {
                        xs: '300px',
                        md: '232px',
                      },
                    },
                    // content
                    '& .MuiCardContent-root, &.MuiCardContent-root:last-child': {
                      padding: 3,
                    },
                    ...cardSx,
                  }}
                  {...restCard}
                >
                  <IconButton
                    onClick={handleClick}
                    aria-label="Close the card"
                    size="small"
                    color={onBackground ? 'inherit' : 'surface'}
                    sx={{
                      position: 'absolute',
                      zIndex: (theme) => theme.zIndex.hotspotCard,
                      top: '8px',
                      right: '8px',
                      ...insideIconButtonSx,
                    }}
                    {...restInsideIconButtonProps}
                  >
                    {insideIcon || (
                      <PlusIcon
                        sx={{
                          transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                          transition: 'transform .3s ease',
                          ...insideIconSx,
                        }}
                        {...restInsideIconProps}
                      />
                    )}
                  </IconButton>
                  {children}
                </Card>
              </Fade>
            )}
          </Popper>
        </Box>
      </ClickAwayListener>
    );
  },
);
